import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { 
  Ship, MapPin, Calendar, Clock, RefreshCw, Plus, Search, 
  ChevronRight, CheckCircle, AlertCircle, Anchor, Navigation,
  Package, Building2, X, Save, ExternalLink, Truck, Plane
} from 'lucide-react';

// Supported shipping lines
const SHIPPING_LINES = [
  { id: 'maersk', name: 'Maersk Line', code: 'MAEU', trackingUrl: 'https://www.maersk.com/tracking/' },
  { id: 'msc', name: 'Mediterranean Shipping Company (MSC)', code: 'MSCU', trackingUrl: 'https://www.msc.com/track-a-shipment' },
  { id: 'cma_cgm', name: 'CMA-CGM', code: 'CMDU', trackingUrl: 'https://www.cma-cgm.com/ebusiness/tracking' },
  { id: 'hapag_lloyd', name: 'Hapag-Lloyd', code: 'HLCU', trackingUrl: 'https://www.hapag-lloyd.com/en/online-business/track/track-by-container-solution.html' },
  { id: 'one', name: 'ONE (Ocean Network Express)', code: 'ONEU', trackingUrl: 'https://ecomm.one-line.com/one-ecom/manage-shipment/cargo-tracking' },
  { id: 'hmm', name: 'HMM (Hyundai Merchant Marine)', code: 'HDMU', trackingUrl: 'https://www.hmm21.com/cms/business/ebiz/trackTrace/trackTrace/index.jsp' },
  { id: 'yang_ming', name: 'Yang Ming', code: 'YMLU', trackingUrl: 'https://www.yangming.com/e-service/Track_Trace/track_trace_cargo_tracking.aspx' },
  { id: 'pennon', name: 'Pennon Shipping Line', code: 'PENN', trackingUrl: null },
  { id: 'sea_bridge', name: 'Sea Bridge Shipping Line', code: 'SBSL', trackingUrl: null },
  { id: 'swen', name: 'Swen Container Line', code: 'SWEN', trackingUrl: null },
  { id: 'econ', name: 'Econ Shipping', code: 'ECON', trackingUrl: null },
];

// Shipment status options
const SHIPMENT_STATUSES = [
  { id: 'booked', label: 'Booked', color: 'gray' },
  { id: 'departed_origin', label: 'Departed Origin', color: 'blue' },
  { id: 'in_transit', label: 'In Transit', color: 'indigo' },
  { id: 'transhipment', label: 'At Transhipment Port', color: 'purple' },
  { id: 'arrived_destination', label: 'Arrived at Destination', color: 'green' },
  { id: 'customs_clearance', label: 'Customs Clearance', color: 'amber' },
  { id: 'delivered', label: 'Delivered', color: 'emerald' },
];

function ShipmentTracking() {
  const { API } = useContext(AppContext);
  const [shipments, setShipments] = useState([]);
  const [salesContracts, setSalesContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [message, setMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  const [formData, setFormData] = useState({
    salesContractId: '',
    containerNumber: '',
    blNumber: '',
    shippingLine: 'maersk',
    vesselName: '',
    voyageNumber: '',
    portOfLoading: '',
    portOfDischarge: '',
    etd: '',
    eta: '',
    status: 'booked',
    notes: ''
  });

  useEffect(() => {
    fetchShipments();
    fetchSalesContracts();
  }, []);

  const fetchShipments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/imports/shipments`);
      setShipments(response.data.data || []);
    } catch (error) {
      console.error('Error fetching shipments:', error);
    }
    setLoading(false);
  };

  const fetchSalesContracts = async () => {
    try {
      const response = await axios.get(`${API}/imports/sales-contracts`);
      setSalesContracts(response.data.data || []);
    } catch (error) {
      console.error('Error fetching sales contracts:', error);
    }
  };

  const handleRefreshTracking = async (shipmentId) => {
    setRefreshing(prev => ({ ...prev, [shipmentId]: true }));
    try {
      const response = await axios.post(`${API}/imports/shipments/${shipmentId}/refresh`, {});
      if (response.data.updated) {
        setMessage({ type: 'success', text: `ETA updated for shipment ${shipmentId}` });
        fetchShipments();
      } else {
        setMessage({ type: 'info', text: 'No updates available for this shipment' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to refresh tracking data' });
    }
    setRefreshing(prev => ({ ...prev, [shipmentId]: false }));
    setTimeout(() => setMessage(null), 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.containerNumber && !formData.blNumber) {
      setMessage({ type: 'error', text: 'Please enter Container Number or BL Number' });
      return;
    }

    try {
      await axios.post(`${API}/imports/shipments`, formData);
      setMessage({ type: 'success', text: 'Shipment added successfully!' });
      setShowAddModal(false);
      resetForm();
      fetchShipments();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to add shipment' });
    }
    setTimeout(() => setMessage(null), 3000);
  };

  const resetForm = () => {
    setFormData({
      salesContractId: '',
      containerNumber: '',
      blNumber: '',
      shippingLine: 'maersk',
      vesselName: '',
      voyageNumber: '',
      portOfLoading: '',
      portOfDischarge: '',
      etd: '',
      eta: '',
      status: 'booked',
      notes: ''
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const getStatusColor = (status) => {
    const statusObj = SHIPMENT_STATUSES.find(s => s.id === status);
    return statusObj?.color || 'gray';
  };

  const getStatusLabel = (status) => {
    const statusObj = SHIPMENT_STATUSES.find(s => s.id === status);
    return statusObj?.label || status;
  };

  const getShippingLineName = (lineId) => {
    const line = SHIPPING_LINES.find(l => l.id === lineId);
    return line?.name || lineId;
  };

  const getShippingLineUrl = (lineId, containerNumber, blNumber) => {
    const line = SHIPPING_LINES.find(l => l.id === lineId);
    if (!line?.trackingUrl) return null;
    return line.trackingUrl;
  };

  const getDaysUntilETA = (eta) => {
    if (!eta) return null;
    const etaDate = new Date(eta);
    const today = new Date();
    const diffTime = etaDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Filter shipments
  const filteredShipments = shipments.filter(s => {
    const matchesSearch = 
      s.containerNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.blNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.vesselName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || s.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // Summary stats
  const inTransitCount = shipments.filter(s => ['in_transit', 'transhipment', 'departed_origin'].includes(s.status)).length;
  const arrivedCount = shipments.filter(s => ['arrived_destination', 'customs_clearance', 'delivered'].includes(s.status)).length;
  const pendingCount = shipments.filter(s => s.status === 'booked').length;

  return (
    <div className="space-y-6" data-testid="shipment-tracking-page">
      {/* Message Alert */}
      {message && (
        <div className={`p-4 rounded-xl flex items-center gap-3 ${
          message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' :
          message.type === 'info' ? 'bg-blue-50 border border-blue-200 text-blue-700' :
          'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {message.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
          {message.text}
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl">
              <Ship className="text-white" size={28} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Shipment Tracking</h1>
              <p className="text-sm text-gray-500">Track container shipments with real-time ETA updates</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-600 text-white rounded-lg hover:from-blue-600 hover:to-cyan-700 transition shadow-sm"
              data-testid="add-shipment-btn"
            >
              <Plus size={18} />
              Add Shipment
            </button>
            <button
              onClick={fetchShipments}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              Refresh All
            </button>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-4 gap-4 mt-6">
          <div className="bg-gray-50 rounded-xl p-4 text-center">
            <p className="text-3xl font-bold text-gray-900">{shipments.length}</p>
            <p className="text-sm text-gray-500">Total Shipments</p>
          </div>
          <div className="bg-blue-50 rounded-xl p-4 text-center">
            <p className="text-3xl font-bold text-blue-600">{inTransitCount}</p>
            <p className="text-sm text-gray-500">In Transit</p>
          </div>
          <div className="bg-green-50 rounded-xl p-4 text-center">
            <p className="text-3xl font-bold text-green-600">{arrivedCount}</p>
            <p className="text-sm text-gray-500">Arrived/Delivered</p>
          </div>
          <div className="bg-amber-50 rounded-xl p-4 text-center">
            <p className="text-3xl font-bold text-amber-600">{pendingCount}</p>
            <p className="text-sm text-gray-500">Pending Departure</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <div className="flex-1 relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by container, BL number, or vessel..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
          >
            <option value="all">All Statuses</option>
            {SHIPMENT_STATUSES.map(status => (
              <option key={status.id} value={status.id}>{status.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Shipments List */}
      {loading ? (
        <div className="bg-white rounded-xl p-8 border border-gray-200 text-center">
          <RefreshCw className="animate-spin mx-auto mb-3 text-blue-500" size={32} />
          <p className="text-gray-500">Loading shipments...</p>
        </div>
      ) : filteredShipments.length === 0 ? (
        <div className="bg-white rounded-xl p-8 border border-gray-200 text-center">
          <Ship className="mx-auto mb-3 text-gray-400" size={48} />
          <p className="text-gray-500 font-medium">No shipments found</p>
          <p className="text-sm text-gray-400 mt-1">Add a shipment to start tracking</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredShipments.map(shipment => {
            const daysUntil = getDaysUntilETA(shipment.eta);
            const statusColor = getStatusColor(shipment.status);
            const trackingUrl = getShippingLineUrl(shipment.shippingLine, shipment.containerNumber, shipment.blNumber);
            
            return (
              <div
                key={shipment.id}
                className={`bg-white rounded-xl border-l-4 border border-gray-200 shadow-sm hover:shadow-md transition-all ${
                  statusColor === 'green' ? 'border-l-green-500' :
                  statusColor === 'blue' ? 'border-l-blue-500' :
                  statusColor === 'indigo' ? 'border-l-indigo-500' :
                  statusColor === 'purple' ? 'border-l-purple-500' :
                  statusColor === 'amber' ? 'border-l-amber-500' :
                  statusColor === 'emerald' ? 'border-l-emerald-500' :
                  'border-l-gray-400'
                }`}
              >
                <div className="p-5">
                  {/* Header Row */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Package className="text-blue-600" size={20} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          {shipment.containerNumber && (
                            <span className="px-2.5 py-1 bg-gray-100 text-gray-800 rounded-md text-sm font-mono font-bold">
                              {shipment.containerNumber}
                            </span>
                          )}
                          {shipment.blNumber && (
                            <span className="px-2.5 py-1 bg-indigo-100 text-indigo-700 rounded-md text-sm font-mono">
                              BL: {shipment.blNumber}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          {getShippingLineName(shipment.shippingLine)}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <span className={`px-3 py-1.5 rounded-full text-xs font-bold ${
                        statusColor === 'green' ? 'bg-green-100 text-green-700' :
                        statusColor === 'blue' ? 'bg-blue-100 text-blue-700' :
                        statusColor === 'indigo' ? 'bg-indigo-100 text-indigo-700' :
                        statusColor === 'purple' ? 'bg-purple-100 text-purple-700' :
                        statusColor === 'amber' ? 'bg-amber-100 text-amber-700' :
                        statusColor === 'emerald' ? 'bg-emerald-100 text-emerald-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {getStatusLabel(shipment.status)}
                      </span>
                    </div>
                  </div>

                  {/* Vessel & Route Info */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Vessel / Voyage</p>
                      <p className="font-medium text-gray-900">
                        {shipment.vesselName || '-'} {shipment.voyageNumber && `/ ${shipment.voyageNumber}`}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Route</p>
                      <p className="font-medium text-gray-900 flex items-center gap-1">
                        <span>{shipment.portOfLoading || '-'}</span>
                        <ChevronRight size={14} className="text-gray-400" />
                        <span>{shipment.portOfDischarge || '-'}</span>
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">ETD</p>
                      <p className="font-medium text-gray-900">{formatDate(shipment.etd)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">ETA</p>
                      <div className="flex items-center gap-2">
                        <p className="font-bold text-lg text-blue-600">{formatDate(shipment.eta)}</p>
                        {daysUntil !== null && daysUntil > 0 && (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                            {daysUntil} days
                          </span>
                        )}
                        {daysUntil !== null && daysUntil <= 0 && (
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">
                            Arrived
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Last Update & Actions */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      {shipment.lastUpdated && (
                        <span className="flex items-center gap-1">
                          <Clock size={14} />
                          Last updated: {formatDate(shipment.lastUpdated)}
                        </span>
                      )}
                      {shipment.salesContractId && (
                        <span className="flex items-center gap-1 text-indigo-600">
                          <Building2 size={14} />
                          Linked to Contract
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleRefreshTracking(shipment.id)}
                        disabled={refreshing[shipment.id]}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition text-sm font-medium disabled:opacity-50"
                      >
                        <RefreshCw size={14} className={refreshing[shipment.id] ? 'animate-spin' : ''} />
                        Refresh ETA
                      </button>
                      {trackingUrl && (
                        <a
                          href={trackingUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition text-sm"
                        >
                          <ExternalLink size={14} />
                          Track on Website
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Shipment Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gradient-to-r from-blue-500 to-cyan-600">
              <div className="flex items-center gap-3">
                <Ship className="text-white" size={24} />
                <h2 className="text-xl font-bold text-white">Add Shipment</h2>
              </div>
              <button
                onClick={() => { setShowAddModal(false); resetForm(); }}
                className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto max-h-[calc(90vh-140px)]">
              {/* Link to Sales Contract */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Link to Sales Contract (Optional)
                </label>
                <select
                  value={formData.salesContractId}
                  onChange={(e) => setFormData(prev => ({ ...prev, salesContractId: e.target.value }))}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  <option value="">-- Select Sales Contract --</option>
                  {salesContracts.map(sc => (
                    <option key={sc.id} value={sc.id}>
                      {sc.contractNumber} - {sc.supplierName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Container & BL Number */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Container Number
                  </label>
                  <input
                    type="text"
                    value={formData.containerNumber}
                    onChange={(e) => setFormData(prev => ({ ...prev, containerNumber: e.target.value.toUpperCase() }))}
                    placeholder="e.g., MSCU1234567"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none font-mono"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    BL Number
                  </label>
                  <input
                    type="text"
                    value={formData.blNumber}
                    onChange={(e) => setFormData(prev => ({ ...prev, blNumber: e.target.value.toUpperCase() }))}
                    placeholder="e.g., MSCUHAM123456"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none font-mono"
                  />
                </div>
              </div>

              {/* Shipping Line */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Shipping Line <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.shippingLine}
                  onChange={(e) => setFormData(prev => ({ ...prev, shippingLine: e.target.value }))}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  required
                >
                  {SHIPPING_LINES.map(line => (
                    <option key={line.id} value={line.id}>{line.name}</option>
                  ))}
                </select>
              </div>

              {/* Vessel & Voyage */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vessel Name
                  </label>
                  <input
                    type="text"
                    value={formData.vesselName}
                    onChange={(e) => setFormData(prev => ({ ...prev, vesselName: e.target.value }))}
                    placeholder="e.g., MAERSK ELBA"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Voyage Number
                  </label>
                  <input
                    type="text"
                    value={formData.voyageNumber}
                    onChange={(e) => setFormData(prev => ({ ...prev, voyageNumber: e.target.value }))}
                    placeholder="e.g., 234E"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>
              </div>

              {/* Ports */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Port of Loading
                  </label>
                  <input
                    type="text"
                    value={formData.portOfLoading}
                    onChange={(e) => setFormData(prev => ({ ...prev, portOfLoading: e.target.value }))}
                    placeholder="e.g., Antwerp"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Port of Discharge
                  </label>
                  <input
                    type="text"
                    value={formData.portOfDischarge}
                    onChange={(e) => setFormData(prev => ({ ...prev, portOfDischarge: e.target.value }))}
                    placeholder="e.g., Nhava Sheva"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ETD (Estimated Departure)
                  </label>
                  <input
                    type="date"
                    value={formData.etd}
                    onChange={(e) => setFormData(prev => ({ ...prev, etd: e.target.value }))}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ETA (Estimated Arrival) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.eta}
                    onChange={(e) => setFormData(prev => ({ ...prev, eta: e.target.value }))}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    required
                  />
                </div>
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  {SHIPMENT_STATUSES.map(status => (
                    <option key={status.id} value={status.id}>{status.label}</option>
                  ))}
                </select>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Any additional notes..."
                  rows={2}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                />
              </div>

              {/* Info Note */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
                <p>
                  <strong>Auto-tracking:</strong> For major shipping lines (Maersk, MSC, CMA-CGM, Hapag-Lloyd, ONE, HMM, Yang Ming), 
                  use the "Refresh ETA" button to get the latest ETA updates.
                </p>
              </div>

              {/* Modal Footer */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => { setShowAddModal(false); resetForm(); }}
                  className="px-4 py-2.5 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-gradient-to-r from-blue-500 to-cyan-600 text-white rounded-lg hover:from-blue-600 hover:to-cyan-700 transition font-medium flex items-center gap-2"
                >
                  <Save size={16} />
                  Add Shipment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ShipmentTracking;
